// /activate — zwei Zielgruppen, zwei Botschaften.
//
// Pending-User sind in aller Regel Tester, die einen Zugang beantragt haben.
// Sie sehen den Stand ihres Antrags, KEINE Kaufaufforderung: die Registrierungs-
// seite hat ihnen persönliche Freischaltung zugesagt, ein Shop-Link an dieser
// Stelle würde das Versprechen zurücknehmen. Das Code-Feld bleibt für sie
// erreichbar, aber eingeklappt — wer gekauft hat, muss seinen Code einlösen
// können (Kauf = Freischaltung, RequireAuth lässt pending sonst nirgends hin).
//
// Aktive User kommen über den Header hierher, um nachzuladen: für sie steht das
// Formular offen, samt Shop-Link.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { APIError, api } from "@/api/client";
import Header from "@/components/Layout/Header";
import { usePublicConfig } from "@/lib/publicConfig";
import { useAuthStore } from "@/stores/authStore";

export default function Activate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loadMe } = useAuthStore();
  const cfg = usePublicConfig();

  const [code, setCode] = useState("");
  const [codeOffen, setCodeOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    granted: number;
    balance: number;
    already: boolean;
  } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.activateLicense(code.trim());
      await loadMe(); // status kann pending → active gewechselt sein
      setSuccess({
        granted: res.credits_granted,
        balance: res.balance,
        already: res.already_active,
      });
    } catch (err) {
      setError(err instanceof APIError ? err.detail : t("activate.failed"));
    } finally {
      setBusy(false);
    }
  };

  const pending = user?.status === "pending";

  return (
    <>
      <Header />
      <div className="flex flex-col items-center px-4 py-16">
        <div className="bg-ca-card rounded-3xl p-10 w-full max-w-md border border-ca-surface-border space-y-6">
          <div>
            <h1 className="text-2xl font-serif font-semibold text-ca-surface-ink tracking-tight">
              {pending ? t("activate.pendingTitle") : t("activate.title")}
            </h1>
            <p className="text-sm text-ca-surface-muted mt-2 leading-relaxed">
              {pending
                ? t("activate.pendingBody", { email: user?.email ?? "" })
                : t("activate.introActive")}
            </p>
            {pending && (
              <p className="text-sm text-ca-surface-muted mt-3 leading-relaxed">
                {t("activate.pendingContact")}{" "}
                <a
                  href="mailto:fh@brain-hr.com"
                  className="text-ca-surface-accent hover:underline"
                >
                  fh@brain-hr.com
                </a>
              </p>
            )}
          </div>

          {success ? (
            <div className="space-y-6">
              <p className="text-ca-surface-ink leading-relaxed">
                {success.already
                  ? t("activate.successAlready", { balance: success.balance })
                  : t("activate.success", {
                      count: success.granted,
                      balance: success.balance,
                    })}
              </p>
              <button
                onClick={() => navigate("/")}
                className="w-full py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] transition-all active:scale-95"
              >
                {t("activate.continue")}
              </button>
            </div>
          ) : pending && !codeOffen ? (
            <button
              type="button"
              onClick={() => setCodeOffen(true)}
              className="w-full py-3 border border-ca-surface-border text-ca-surface-muted hover:text-ca-surface-accent hover:border-ca-surface-accent text-xs font-black uppercase tracking-widest rounded-xl transition"
            >
              {t("activate.codeToggle")}
            </button>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
                  {t("activate.codeLabel")}
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t("activate.codePlaceholder")}
                  autoComplete="off"
                  className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink font-mono outline-none focus:border-ca-accent placeholder:text-ca-surface-muted"
                  required
                  minLength={4}
                />
              </div>
              {error && <p className="text-verdict-red text-sm">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] disabled:opacity-50 transition-all active:scale-95"
              >
                {busy ? t("activate.submitting") : t("activate.submit")}
              </button>
              {cfg?.shop_url && !pending && (
                <p className="text-center text-sm text-ca-surface-muted">
                  {t("activate.shopHint")}{" "}
                  <a
                    href={cfg.shop_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ca-surface-accent hover:underline"
                  >
                    {t("activate.shopLink")}
                  </a>
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
}
